import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Campaign, CampaignEnrollPreview, CampaignStats } from '@/api/types';
import {
  CAMPAIGN_SEND_RAIL_LABELS,
  CAMPAIGN_TYPE_LABELS,
  MAX_RUNNING_CAMPAIGNS_PER_PROJECT,
  capabilityDeniedMessage,
} from '@marketing-hub/shared';
import { useProjectRole } from '@/project/useProjectRole';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { useToast } from '@/components/toast/ToastProvider';
import { CampaignReadinessPanel } from '@/features/campaigns/CampaignReadinessPanel';
import { CampaignSimulatePanel } from '@/features/campaigns/CampaignSimulatePanel';
import { CampaignBrevoPanel } from '@/features/campaigns/CampaignBrevoPanel';
import { CampaignSequenceSendRailPanel } from '@/features/campaigns/CampaignSequenceSendRailPanel';
import type { IntegrationPublic } from '@marketing-hub/shared';
import { CampaignStatusBadge } from '@/features/campaigns/CampaignStatusBadge';
import {
  CampaignKpiStrip,
  CampaignStatsPanel,
} from '@/features/campaigns/CampaignStatsPanel';
import { CampaignEnrollmentsTable } from '@/features/campaigns/CampaignEnrollmentsTable';
import { CampaignHowItWorksCallout } from '@/features/campaigns/CampaignHowItWorksCallout';
import { formatSendWindow } from '@/features/campaigns/campaignUi';
import { labelCampaignPauseReason } from '@/features/campaigns/enrollmentLabels';
import { useOutreachReadiness } from '@/features/campaigns/useOutreachReadiness';
import { CampaignStartChecklistModal } from '@/features/campaigns/CampaignStartChecklistModal';
import campStyles from '@/features/campaigns/Campaigns.module.css';
import styles from '../ProjectsPage.module.css';

const TABS = [
  { id: 'overview', label: 'Overzicht' },
  { id: 'enrollment', label: 'Leads & funnel' },
  { id: 'performance', label: 'Prestaties' },
  { id: 'settings', label: 'Instellingen' },
  { id: 'log', label: 'Send-log' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function CampaignCommandPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<TabId>('overview');
  const [simulateVariantId, setSimulateVariantId] = useState('');
  const [startChecklistOpen, setStartChecklistOpen] = useState(false);

  const integrationsQ = useQuery({
    queryKey: ['integrations', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ integrations: IntegrationPublic[] }>(
        `/api/integrations?project_id=${projectId}`,
      ),
  });

  const hasBrevoIntegration = Boolean(
    integrationsQ.data?.integrations.some(
      (i) => i.connector_type === 'brevo' && i.status === 'active',
    ),
  );

  const campaignsQ = useQuery({
    queryKey: ['campaigns', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>(`/api/campaigns?project_id=${projectId}`),
  });

  const campaign = (campaignsQ.data?.campaigns ?? []).find((c) => c.id === campaignId);
  const campaignSendRail = campaign?.send_rail ?? 'smtp';
  const readinessQ = useOutreachReadiness(projectId, campaignSendRail);
  const runningCount = (campaignsQ.data?.campaigns ?? []).filter((c) => c.status === 'running')
    .length;
  const { can: canProject } = useProjectRole(projectId);

  const projectQ = useQuery({
    queryKey: ['project', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        project: { inbound_enroll?: { enabled?: boolean; campaign_name?: string } };
      }>(`/api/projects/${projectId}`),
  });

  const previewQ = useQuery({
    queryKey: ['campaign-preview', campaignId],
    enabled: Boolean(campaignId),
    queryFn: () =>
      apiFetch<{ preview: CampaignEnrollPreview }>(`/api/campaigns/${campaignId}/enroll-preview`),
  });

  const statsQ = useQuery({
    queryKey: ['campaign-stats', campaignId],
    enabled: Boolean(campaignId),
    queryFn: () => apiFetch<{ stats: CampaignStats }>(`/api/campaigns/${campaignId}/stats`),
  });

  const experimentQ = useQuery({
    queryKey: ['campaign-experiment', campaignId],
    enabled: Boolean(campaignId),
    queryFn: () =>
      apiFetch<{
        experiment: import('@/api/types').CampaignExperiment | null;
        variants: import('@/api/types').CampaignExperimentVariant[];
      }>(`/api/campaigns/${campaignId}/experiment`),
  });

  const stepExperimentQ = useQuery({
    queryKey: ['campaign-step-experiment', campaignId],
    enabled: Boolean(campaignId),
    queryFn: () =>
      apiFetch<{
        experiment: import('@/api/types').CampaignStepExperiment | null;
        variants: import('@/api/types').CampaignStepExperimentVariant[];
        featureDisabled?: boolean;
      }>(`/api/campaigns/${campaignId}/step-experiment`),
  });

  const enrollmentsQ = useQuery({
    queryKey: ['campaign-enrollments', campaignId],
    enabled: Boolean(campaignId) && (tab === 'enrollment' || tab === 'performance'),
    queryFn: () =>
      apiFetch<{ enrollments: import('@/features/campaigns/CampaignEnrollmentsTable').CampaignEnrollmentRow[] }>(
        `/api/campaigns/${campaignId}/enrollments`,
      ),
  });

  const sendLogQ = useQuery({
    queryKey: ['campaign-send-log', campaignId],
    enabled: Boolean(campaignId) && tab === 'log',
    queryFn: () =>
      apiFetch<{ log: { id: string; level: string; message: string; created_at: string }[] }>(
        `/api/campaigns/${campaignId}/send-log`,
      ),
  });

  const startCampaign = useMutation({
    mutationFn: () =>
      apiFetch<{ enrolled?: number; skipped_overlap?: number }>(
        `/api/campaigns/${campaignId}/start`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => {
      toast.push(
        `Campagne gestart — ${data.enrolled ?? 0} enrolled, overlap ${data.skipped_overlap ?? 0}`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
      void qc.invalidateQueries({ queryKey: ['campaign-preview', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaign-enrollments', campaignId] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const enrollList = useMutation({
    mutationFn: () =>
      apiFetch<{ enrolled: number; skipped_overlap: number }>(
        `/api/campaigns/${campaignId}/enroll-list`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => {
      toast.push(
        `Lijst gesynchroniseerd — ${data.enrolled} nieuw, overlap ${data.skipped_overlap}`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['campaign-preview', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaign-stats', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaign-enrollments', campaignId] });
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const pauseCampaign = useMutation({
    mutationFn: () =>
      apiFetch(`/api/campaigns/${campaignId}/pause`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      toast.push('Campagne gepauzeerd', 'success');
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
    },
  });

  const deleteCampaign = useMutation({
    mutationFn: () => apiFetch(`/api/campaigns/${campaignId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.push('Campagne verwijderd', 'success');
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
      navigate('/app/campagnes');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const patchSettings = useMutation({
    mutationFn: (payload: {
      send_hour_start: number;
      send_hour_end: number;
      weekdays_only: boolean;
      exit_on_open_deal: boolean;
      send_rail?: 'smtp' | 'brevo_transactional';
    }) =>
      apiFetch(`/api/campaigns/${campaignId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      toast.push('Instellingen opgeslagen', 'success');
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
      void qc.invalidateQueries({ queryKey: ['outreach-readiness', projectId] });
    },
  });

  if (!campaignId) return <ErrorState message="Campagne ontbreekt in URL" />;
  if (campaignsQ.isLoading) return <LoadingState />;
  if (!campaign) return <ErrorState message="Campagne niet gevonden voor dit project" />;

  const stats = statsQ.data?.stats;
  const readiness = readinessQ.data?.readiness;
  const canStartDraft =
    campaign.status === 'draft' &&
    readiness?.ready === true &&
    runningCount < MAX_RUNNING_CAMPAIGNS_PER_PROJECT;
  const startBlockedReason =
    campaign.status === 'draft' && !readiness?.ready
      ? 'Outreach readiness niet OK'
      : campaign.status === 'draft' && runningCount >= MAX_RUNNING_CAMPAIGNS_PER_PROJECT
        ? `Max ${MAX_RUNNING_CAMPAIGNS_PER_PROJECT} running campagnes`
        : null;

  const inboundEnroll = projectQ.data?.project?.inbound_enroll;

  const typeLabel =
    CAMPAIGN_TYPE_LABELS[campaign.type as keyof typeof CAMPAIGN_TYPE_LABELS] ?? campaign.type;

  return (
    <div>
      {startChecklistOpen && projectId ? (
        <CampaignStartChecklistModal
          campaign={campaign}
          projectId={projectId}
          runningCount={runningCount}
          confirming={startCampaign.isPending}
          onCancel={() => setStartChecklistOpen(false)}
          onConfirm={() => {
            startCampaign.mutate(undefined, {
              onSuccess: () => setStartChecklistOpen(false),
            });
          }}
        />
      ) : null}
      <p className={campStyles.breadcrumbs}>
        <Link to="/app/campagnes">Campagnes</Link>
        <span aria-hidden="true"> / </span>
        <span>{campaign.name}</span>
      </p>

      <header className={campStyles.commandHeader}>
        <div className={campStyles.commandTitleBlock}>
          <h1 className={campStyles.commandTitle}>{campaign.name}</h1>
          <div className={campStyles.commandMeta}>
            <CampaignStatusBadge status={campaign.status} />
            <span className={campStyles.metaChip}>{typeLabel}</span>
            {campaign.type !== 'broadcast' ? (
              <span className={campStyles.metaChip}>
                {CAMPAIGN_SEND_RAIL_LABELS[campaignSendRail]}
              </span>
            ) : (
              <span className={campStyles.metaChip}>Brevo broadcast</span>
            )}
            <span className={campStyles.metaChip}>
              {formatSendWindow(campaign.send_hour_start, campaign.send_hour_end)}
            </span>
          </div>
          {campaign.status === 'paused' && campaign.pause_reason ? (
            <p className={styles.mutedSmall} style={{ marginTop: 'var(--space-sm)' }}>
              {labelCampaignPauseReason(campaign.pause_reason)}
            </p>
          ) : null}
        </div>
        <div className={campStyles.commandActions}>
          {campaign.status === 'draft' ? (
            <Button
              type="button"
              onClick={() => setStartChecklistOpen(true)}
              disabled={startCampaign.isPending || !canProject('campaigns_start')}
              title={
                !canProject('campaigns_start')
                  ? capabilityDeniedMessage('campaigns_start')
                  : (startBlockedReason ?? undefined)
              }
            >
              Start campagne
            </Button>
          ) : null}
          {campaign.status === 'paused' ? (
            <Button
              type="button"
              onClick={() => startCampaign.mutate()}
              disabled={startCampaign.isPending}
            >
              Hervat
            </Button>
          ) : null}
          {campaign.status === 'running' ? (
            <Button type="button" variant="ghost" onClick={() => pauseCampaign.mutate()}>
              Pauze
            </Button>
          ) : null}
          <Link to="/app/campagnes/bibliotheek">
            <Button type="button" variant="ghost">
              Bibliotheek
            </Button>
          </Link>
        </div>
      </header>

      {startBlockedReason ? (
        <div className={campStyles.alertBanner} role="status">
          {startBlockedReason}. Controleer readiness rechts of open Deliverability.
        </div>
      ) : null}

      <CampaignKpiStrip stats={stats} loading={statsQ.isLoading} />

      <nav className={campStyles.subNav} aria-label="Campagne detail">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={[campStyles.subNavLink, tab === t.id ? campStyles.subNavLinkActive : ''].join(
              ' ',
            )}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className={campStyles.commandGrid}>
        <div>
          {tab === 'overview' ? (
            <>
              <CampaignBrevoPanel campaign={campaign} hasBrevoIntegration={hasBrevoIntegration} />
              <CampaignSequenceSendRailPanel
                campaign={campaign}
                hasBrevoIntegration={hasBrevoIntegration}
              />
              {experimentQ.data?.experiment ? (
                <section className={campStyles.panel}>
                  <div className={campStyles.panelHead}>
                    <h2 className={campStyles.panelTitle}>A/B-experiment (sequence)</h2>
                    <span className={campStyles.metaChip}>{experimentQ.data.experiment.status}</span>
                  </div>
                  <div className={campStyles.panelBody}>
                    <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
                      {experimentQ.data.experiment.name} · metric{' '}
                      {experimentQ.data.experiment.primary_metric.replace(/_/g, ' ')} ·{' '}
                      {experimentQ.data.experiment.experiment_type.replace(/_/g, ' ')}
                    </p>
                    <div className={campStyles.tableWrap}>
                      <table className={campStyles.dataTable}>
                        <thead>
                          <tr>
                            <th>Variant</th>
                            <th>Gewicht</th>
                            <th>Sequence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {experimentQ.data.variants.map((v) => (
                            <tr key={v.id}>
                              <td>
                                <strong>{v.variant_key}</strong>
                                {v.label ? ` — ${v.label}` : ''}
                              </td>
                              <td>{v.weight}</td>
                              <td className={campStyles.mutedInline}>{v.sequence_id.slice(0, 8)}…</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              ) : null}
              {stepExperimentQ.data?.experiment ? (
                <section className={campStyles.panel}>
                  <div className={campStyles.panelHead}>
                    <h2 className={campStyles.panelTitle}>Stap A/B-experiment</h2>
                    <span className={campStyles.metaChip}>{stepExperimentQ.data.experiment.status}</span>
                  </div>
                  <div className={campStyles.panelBody}>
                    <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
                      {stepExperimentQ.data.experiment.name} · stap{' '}
                      {stepExperimentQ.data.experiment.step_order} · metric{' '}
                      {stepExperimentQ.data.experiment.primary_metric.replace(/_/g, ' ')}
                    </p>
                    <div className={campStyles.tableWrap}>
                      <table className={campStyles.dataTable}>
                        <thead>
                          <tr>
                            <th>Variant</th>
                            <th>Gewicht</th>
                            <th>Template</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stepExperimentQ.data.variants.map((v) => (
                            <tr key={v.id}>
                              <td>
                                <strong>{v.variant_key}</strong>
                                {v.label ? ` — ${v.label}` : ''}
                              </td>
                              <td>{v.weight}</td>
                              <td className={campStyles.mutedInline}>
                                {v.email_template_id
                                  ? `${v.email_template_id.slice(0, 8)}…`
                                  : 'Inline copy'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              ) : stepExperimentQ.data?.featureDisabled ? (
                <p className={campStyles.panelBodyMuted}>
                  Stap A/B uit — zet feature flag <code>step_experiments_v1</code> aan (platform).
                </p>
              ) : null}
              <CampaignHowItWorksCallout />
            </>
          ) : null}

          {tab === 'performance' ? (
            <>
              <CampaignStatsPanel stats={stats} loading={statsQ.isLoading} />
              <CampaignHowItWorksCallout />
            </>
          ) : null}

          {tab === 'enrollment' ? (
            <section className={campStyles.panel}>
              <div className={campStyles.panelHead}>
                <h2 className={campStyles.panelTitle}>Leads in deze campagne</h2>
              </div>
              <div className={campStyles.panelBody}>
                <CampaignEnrollmentsTable
                  rows={enrollmentsQ.data?.enrollments ?? []}
                  loading={enrollmentsQ.isLoading}
                />
                <div className={campStyles.analysisBlock} style={{ marginTop: 'var(--space-lg)' }}>
                  <h3 className={campStyles.analysisTitle}>Audience (lijst)</h3>
                {previewQ.data?.preview ? (
                  <EnrollmentPreviewGrid preview={previewQ.data.preview} />
                ) : (
                  <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
                    Preview laden…
                  </p>
                )}
                </div>
                <div className={campStyles.commandActions}>
                  {campaign.status === 'running' ? (
                    <Button
                      type="button"
                      onClick={() => enrollList.mutate()}
                      disabled={enrollList.isPending}
                    >
                      Sync lijst → enroll
                    </Button>
                  ) : (
                    <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
                      Start de campagne om de audience te enrollen; daarna sync voor nieuwe leden.
                    </p>
                  )}
                </div>
                <div className={campStyles.analysisBlock}>
                  <h3 className={campStyles.analysisTitle}>Inbound auto-enroll</h3>
                  <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
                    {inboundEnroll?.enabled
                      ? `Actief → campagne “${inboundEnroll.campaign_name ?? '—'}”`
                      : 'Niet geconfigureerd — stel in via projectinstellingen.'}
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {tab === 'settings' ? (
            <section className={campStyles.panel}>
              <div className={campStyles.panelHead}>
                <h2 className={campStyles.panelTitle}>Verzendinstellingen & test</h2>
              </div>
              <div className={campStyles.panelBody}>
              <form
                className={styles.formGrid}
                onSubmit={(e: FormEvent<HTMLFormElement>) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  const rail = fd.get('sendRail');
                  patchSettings.mutate({
                    send_hour_start: Number(fd.get('hStart')),
                    send_hour_end: Number(fd.get('hEnd')),
                    weekdays_only: fd.get('weekdays') === 'on',
                    exit_on_open_deal: fd.get('exitDeal') === 'on',
                    send_rail:
                      rail === 'brevo_transactional' ? 'brevo_transactional' : 'smtp',
                  });
                }}
              >
                {campaign.type !== 'broadcast' ? (
                  <Field
                    label="Verzendrail (sequence)"
                    hint="Brevo transactional: geen eigen mailbox nodig; limieten via Brevo-integratie"
                  >
                    <select
                      name="sendRail"
                      defaultValue={campaign.send_rail ?? 'smtp'}
                      disabled={campaign.status === 'running'}
                    >
                      <option value="smtp">{CAMPAIGN_SEND_RAIL_LABELS.smtp}</option>
                      <option value="brevo_transactional">
                        {CAMPAIGN_SEND_RAIL_LABELS.brevo_transactional}
                      </option>
                    </select>
                  </Field>
                ) : null}
                <Field
                  label="Verzend uur start"
                  hint="Send window gebruikt lead-timezone (Compliance-tab) indien gezet; anders project-default."
                >
                  <TextInput
                    name="hStart"
                    type="number"
                    min={0}
                    max={23}
                    defaultValue={campaign.send_hour_start ?? 9}
                  />
                </Field>
                <Field label="Verzend uur eind">
                  <TextInput
                    name="hEnd"
                    type="number"
                    min={0}
                    max={23}
                    defaultValue={campaign.send_hour_end ?? 17}
                  />
                </Field>
                <label className={styles.mutedSmall}>
                  <input
                    name="weekdays"
                    type="checkbox"
                    defaultChecked={campaign.weekdays_only ?? true}
                  />{' '}
                  Alleen werkdagen
                </label>
                <label className={styles.mutedSmall}>
                  <input
                    name="exitDeal"
                    type="checkbox"
                    defaultChecked={campaign.exit_on_open_deal ?? true}
                  />{' '}
                  Stop bij open deal
                </label>
                <Button type="submit">Opslaan</Button>
              </form>
              {projectId && campaignId ? (
                <CampaignSimulatePanel
                  projectId={projectId}
                  campaignId={campaignId}
                  showSeedSend
                  variantId={simulateVariantId || undefined}
                  onVariantIdChange={setSimulateVariantId}
                  experimentVariants={experimentQ.data?.variants?.map((v) => ({
                    id: v.id,
                    variant_key: v.variant_key,
                  }))}
                />
              ) : null}
              {campaign.status !== 'running' && campaign.status !== 'scheduled' ? (
                <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-mist)' }}>
                  <h3 className={campStyles.panelTitle}>Campagne verwijderen</h3>
                  <p className={campStyles.panelBodyMuted}>
                    Verwijdert enrollments en gekoppelde experiment-data. Lijst en sequence blijven in de
                    bibliotheek.
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!canProject('campaigns_draft') || deleteCampaign.isPending}
                    title={!canProject('campaigns_draft') ? 'Geen rechten om te verwijderen' : undefined}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Campagne "${campaign.name}" definitief verwijderen? Dit kan niet ongedaan.`,
                        )
                      ) {
                        deleteCampaign.mutate();
                      }
                    }}
                  >
                    {deleteCampaign.isPending ? 'Verwijderen…' : 'Campagne verwijderen'}
                  </Button>
                </div>
              ) : (
                <p className={campStyles.panelBodyMuted} style={{ marginTop: '1rem' }}>
                  Pauzeer een actieve campagne vóór je hem verwijdert.
                </p>
              )}
              </div>
            </section>
          ) : null}

          {tab === 'log' ? (
            <section className={campStyles.panel}>
              <div className={campStyles.panelHead}>
                <h2 className={campStyles.panelTitle}>Send-log</h2>
                <span className={campStyles.metaChip}>Laatste 50 regels</span>
              </div>
              {sendLogQ.isLoading ? (
                <LoadingState />
              ) : (
                <div className={campStyles.tableWrap}>
                  <table className={`${campStyles.dataTable} ${campStyles.logTable}`}>
                    <thead>
                      <tr>
                        <th>Tijd</th>
                        <th>Niveau</th>
                        <th>Bericht</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sendLogQ.data?.log ?? []).slice(0, 50).map((line) => (
                        <tr key={line.id}>
                          <td>{new Date(line.created_at).toLocaleString('nl-NL')}</td>
                          <td>
                            <span
                              className={[
                                campStyles.logLevel,
                                line.level === 'error'
                                  ? campStyles.logLevel_error
                                  : line.level === 'warn'
                                    ? campStyles.logLevel_warn
                                    : campStyles.logLevel_info,
                              ].join(' ')}
                            >
                              {line.level}
                            </span>
                          </td>
                          <td>{line.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}
        </div>

        {projectId ? (
          <CampaignReadinessPanel projectId={projectId} sendRail={campaignSendRail} />
        ) : null}
      </div>
    </div>
  );
}

function EnrollmentPreviewGrid({ preview }: { preview: CampaignEnrollPreview }) {
  return (
    <div className={campStyles.kpiStrip}>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Geschikt</p>
        <p className={campStyles.kpiValue}>
          {preview.eligible}
          <span className={campStyles.mutedInline}> / {preview.total}</span>
        </p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Suppressie</p>
        <p className={campStyles.kpiValue}>{preview.suppressed}</p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>DNC</p>
        <p className={campStyles.kpiValue}>{preview.dnc}</p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Geen e-mail</p>
        <p className={campStyles.kpiValue}>{preview.no_email}</p>
      </div>
    </div>
  );
}
